import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Typography,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { fr } from 'date-fns/locale';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { DateRange } from '../models/CustomerStats';
import DateUtils from '../utils/dateUtils';

interface DateRangePickerProps {
  value?: DateRange;
  onChange: (dateRange: DateRange | undefined) => void;
  label?: string;
  showQuickOptions?: boolean;
  includeTime?: boolean;
  includeSalesDay?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value,
  onChange,
  label = "Période",
  showQuickOptions = true,
  includeTime = false,
  includeSalesDay = false
}) => {
  const [selectedPreset, setSelectedPreset] = useState<string>('');
  const [customStartDate, setCustomStartDate] = useState<Date | null>(value?.start || null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(value?.end || null);
  const [isCustomMode, setIsCustomMode] = useState(false);

  const dateRangeOptions = DateUtils.getDateRangeOptions({ includeSalesDay });

  const handlePresetChange = (presetValue: string) => {
    setSelectedPreset(presetValue);
    setIsCustomMode(false);

    if (presetValue === '') {
      onChange(undefined);
      return;
    }

    if (presetValue === 'custom') {
      setIsCustomMode(true);
      return;
    }

    const option = dateRangeOptions.find(opt => opt.value === presetValue);
    if (option) {
      onChange(option.range);
    }
  };

  const handleCustomDateChange = () => {
    if (customStartDate && customEndDate) {
      const customRange = includeTime
        ? DateUtils.createCustomDateTimeRange(customStartDate, customEndDate)
        : DateUtils.createCustomRange(customStartDate, customEndDate);
      const validation = DateUtils.validateDateRange(customRange);
      
      if (validation.isValid) {
        onChange(customRange);
      } else {
        console.error('Plage de dates invalide:', validation.error);
      }
    }
  };

  const handleClear = () => {
    setSelectedPreset('');
    setCustomStartDate(null);
    setCustomEndDate(null);
    setIsCustomMode(false);
    onChange(undefined);
  };

  const getCurrentDescription = (): string => {
    if (!value) return 'Toutes les périodes';
    return includeTime
      ? DateUtils.getDateTimeRangeDescription(value)
      : DateUtils.getDateRangeDescription(value);
  };

  const customRange = customStartDate && customEndDate
    ? (includeTime
        ? DateUtils.createCustomDateTimeRange(customStartDate, customEndDate)
        : DateUtils.createCustomRange(customStartDate, customEndDate))
    : undefined;
  const customValidation = customRange
    ? DateUtils.validateDateRange(customRange)
    : { isValid: false };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={fr}>
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CalendarTodayIcon color="primary" />
          <Typography variant="h6">{label}</Typography>
          {value && (
            <Chip 
              label={getCurrentDescription()}
              onDelete={handleClear}
              color="primary"
              variant="outlined"
              size="small"
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {showQuickOptions && (
            <Box sx={{ width: '100%' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Période prédéfinie</InputLabel>
                <Select
                  value={selectedPreset}
                  onChange={(e) => handlePresetChange(e.target.value)}
                  label="Période prédéfinie"
                >
                  <MenuItem value="">
                    <em>Toutes les périodes</em>
                  </MenuItem>
                  {dateRangeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                  <MenuItem value="custom">
                    Période personnalisée...
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {(isCustomMode || !showQuickOptions) && (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' }, 
              gap: 2,
              alignItems: 'center'
            }}>
              <Box sx={{ flex: 1 }}>
                {includeTime ? (
                  <DateTimePicker
                    label="Date et heure de début"
                    value={customStartDate}
                    onChange={(date) => setCustomStartDate(date)}
                    ampm={false}
                    format="dd/MM/yyyy HH:mm"
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true
                      }
                    }}
                    maxDate={new Date()}
                  />
                ) : (
                  <DatePicker
                    label="Date de début"
                    value={customStartDate}
                    onChange={(date) => setCustomStartDate(date)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true
                      }
                    }}
                    maxDate={new Date()}
                  />
                )}
              </Box>

              <Box sx={{ flex: 1 }}>
                {includeTime ? (
                  <DateTimePicker
                    label="Date et heure de fin"
                    value={customEndDate}
                    onChange={(date) => setCustomEndDate(date)}
                    ampm={false}
                    format="dd/MM/yyyy HH:mm"
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        error: Boolean(customRange && !customValidation.isValid),
                        helperText: customRange && !customValidation.isValid
                          ? customValidation.error
                          : 'La minute de fin est incluse'
                      }
                    }}
                    minDateTime={customStartDate || undefined}
                    maxDate={new Date()}
                  />
                ) : (
                  <DatePicker
                    label="Date de fin"
                    value={customEndDate}
                    onChange={(date) => setCustomEndDate(date)}
                    slotProps={{
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        error: Boolean(customRange && !customValidation.isValid),
                        helperText: customRange && !customValidation.isValid
                          ? customValidation.error
                          : undefined
                      }
                    }}
                    minDate={customStartDate || undefined}
                    maxDate={new Date()}
                  />
                )}
              </Box>

              <Box sx={{ minWidth: 120 }}>
                <Button
                  variant="contained"
                  onClick={handleCustomDateChange}
                  disabled={!customStartDate || !customEndDate || !customValidation.isValid}
                  fullWidth
                  size="small"
                >
                  Appliquer
                </Button>
              </Box>
            </Box>
          )}

          {value && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleClear}
                size="small"
                sx={{ minWidth: 120 }}
              >
                Effacer
              </Button>
            </Box>
          )}
        </Box>

        {value && (
          <Box sx={{ mt: 2, p: 1, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Période sélectionnée :</strong> {getCurrentDescription()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Du {DateUtils.formatDateTimeFR(value.start)} au {DateUtils.formatDateTimeFR(value.end)}
            </Typography>
          </Box>
        )}
      </Paper>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
